import {
  isOrganizationAdmin,
  type OrganizationRole,
  type UserOrganization,
} from "@mountayaapp/api";

/**
 * OrganizationAccess is how the viewer holds the organization an organization page shows. A
 * member holds it through an accepted membership; a Mountaya staff member who is no member
 * holds it through the staff read waiver, which opens every read and no write. Both can be
 * true at once, a staff member who is also a member, and then the membership decides what the
 * pages let them change while `staff` decides whether the terms of the agreement are theirs to
 * edit.
 *
 * - membership — the accepted membership, or null on a staff visit.
 * - organizationId — the organization in view.
 * - organizationName — its display name, off the membership or off the staff read.
 * - staff — whether the viewer is Mountaya staff, as the staff status read answered.
 */
export interface OrganizationAccess {
  readonly membership: UserOrganization | null;
  readonly organizationId: string;
  readonly organizationName: string;
  readonly staff: boolean;
}

/**
 * OrganizationAccessInput carries what the organization guard settled before deciding: the
 * viewer's memberships, the organization the route names, whether the viewer is staff, and
 * the organization's name as the staff read answered it, null when no such read was made or
 * it was refused.
 */
export interface OrganizationAccessInput {
  readonly memberships: readonly UserOrganization[];
  readonly organizationId: string;
  readonly staff: boolean;
  readonly staffReadName: string | null;
}

/**
 * findActiveMembership returns the viewer's membership in the organization, or null. Every
 * membership listed is one the viewer has joined: an invitation waiting for an answer is no
 * membership and no way in, it is accepted from the notifications, not entered.
 */
export const findActiveMembership = (
  memberships: readonly UserOrganization[],
  organizationId: string,
): UserOrganization | null =>
  memberships.find((candidate) => candidate.organization_id === organizationId) ?? null;

/**
 * resolveOrganizationAccess decides whether the viewer may open the organization, and how. A
 * membership opens it whatever the staff answer is. Without one, only staff get in, and only
 * when the staff read of the organization answered: the waiver never covers the shared
 * personal-accounts organization, and an unknown or canceled organization answers not found
 * to staff as to anyone, so a refused read is a closed door rather than a page that cannot
 * load. Null means the viewer has no way in.
 */
export const resolveOrganizationAccess = ({
  memberships,
  organizationId,
  staff,
  staffReadName,
}: OrganizationAccessInput): OrganizationAccess | null => {
  const membership = findActiveMembership(memberships, organizationId);
  if (membership !== null) {
    return {
      membership,
      organizationId,
      organizationName: membership.organization_name,
      staff,
    };
  }

  if (staff && staffReadName !== null) {
    return { membership: null, organizationId, organizationName: staffReadName, staff: true };
  }

  return null;
};

/**
 * accessRole is the viewer's role in the organization, or null on a staff visit, where the
 * viewer holds no role at all.
 */
export const accessRole = (access: OrganizationAccess): OrganizationRole | null =>
  access.membership?.role ?? null;

/**
 * canManageOrganization reports whether the viewer may run the organization's own
 * administration (members, keys, settings): an owner or an admin. Staff alone never may,
 * because staff write only the terms of the agreement, through the staff routes.
 */
export const canManageOrganization = (access: OrganizationAccess): boolean => {
  const role = accessRole(access);

  return role !== null && isOrganizationAdmin(role);
};

/**
 * isStaffVisit reports whether the viewer opened the organization as staff without belonging
 * to it, the visit that is neither remembered as the viewer's own organization nor attributed
 * to the customer.
 */
export const isStaffVisit = (access: OrganizationAccess): boolean => access.membership === null;
